import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  Download,
  Timer,
  ClipboardList,
  Clock,
  CheckCircle,
  DollarSign
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import Breadcrumb from '../../components/Common/Breadcrumb'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import overtimeService from '../../services/overtimeService'
import '../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const Overtime = () => {
  const navigate = useNavigate()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalOvertimeRecords: 0,
    totalOvertimeHours: 0,
    totalCompensationAmount: 0,
    totalTimeOffHours: 0,
    draftRecords: 0,
    submittedRecords: 0,
    approvedRecords: 0,
    rejectedRecords: 0,
    paidRecords: 0,
    pendingApprovalHours: 0,
    pendingApprovalAmount: 0
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [overtimeResponse, statsResponse] = await Promise.all([
        overtimeService.getAllOvertime(),
        overtimeService.getOvertimeStats()
      ])
      
      if (overtimeResponse.success) {
        // Transform _id to id for frontend compatibility
        const transformedData = (overtimeResponse.data || []).map(overtime => ({
          ...overtime,
          id: overtime._id
        }))
        setData(transformedData)
        // Only show toast if data is empty and we haven't shown it before
        if (overtimeResponse.data?.length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No overtime records found. Create your first overtime record!')
        }
      } else {
        toast.error(overtimeResponse.message || 'Failed to load overtime records')
      }
      
      if (statsResponse.success) {
        setStats(statsResponse.data)
      } else {
        toast.error(statsResponse.message || 'Failed to load statistics')
      }
    } catch (error) {
      console.error('Error loading overtime records:', error)
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view overtime records')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to load overtime records')
        }
      }
    } finally {
      setLoading(false)
    }
  }

  const formatHours = (hours) => {
    if (!hours) return '0.0h'
    return `${parseFloat(hours).toFixed(1)}h`
  }

  const formatCurrency = (amount, currency = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount || 0)
  }

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      'Draft': 'inactive',
      'Submitted': 'pending',
      'Approved': 'active',
      'Rejected': 'danger',
      'Paid': 'active'
    }
    return statusMap[status] || 'inactive'
  }

  const getOvertimeTypeBadgeClass = (type) => {
    const typeMap = {
      'Weekday': 'pending',
      'Weekend': 'warning',
      'Holiday': 'danger',
      'Night Shift': 'inactive'
    }
    return typeMap[type] || 'inactive'
  }

  const columns = [
    {
      accessorKey: 'overtimeNumber',
      header: 'Overtime Number',
      cell: ({ getValue }) => (
        <div className="service-code-cell" style={{ textAlign: 'left' }}>
          <span className="code-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'employeeName',
      header: 'Employee',
      cell: ({ getValue, row }) => (
        <div className="service-description-cell" style={{ textAlign: 'left' }}>
          <span className="description-text">{getValue() || '-'}</span>
          {row.original.employeeCode && (
            <div style={{ fontSize: '12px', color: 'var(--color-text-secondary, #5B6478)' }}>{row.original.employeeCode}</div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'overtimeDate',
      header: 'Date',
      cell: ({ getValue }) => {
        const date = getValue()
        return (
          <div style={{ textAlign: 'center' }}>
            {date ? new Date(date).toLocaleDateString() : '-'}
          </div>
        )
      },
    },
    {
      accessorKey: 'overtimeHours',
      header: 'Overtime Hours',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'center', fontWeight: '600', color: '#dc3545' }}>
          {formatHours(getValue())}
        </div>
      ),
    },
    {
      accessorKey: 'overtimeType',
      header: 'Type',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${getOvertimeTypeBadgeClass(getValue())}`}>
            {getValue() || '-'}
          </span>
        </div>
      ),
    },
    {
      accessorKey: 'compensationType',
      header: 'Compensation',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'center' }}>
          {getValue() || '-'}
        </div>
      ),
    },
    {
      accessorKey: 'compensationAmount',
      header: 'Amount',
      cell: ({ getValue, row }) => (
        <div style={{ textAlign: 'right', fontWeight: '600' }}>
          {formatCurrency(getValue(), row.original.currency || 'INR')}
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${getStatusBadgeClass(getValue())}`}>
            {getValue() || '-'}
          </span>
        </div>
      ),
    },
  ]

  const handleAdd = () => {
    navigate('/timesheets/overtime/form')
  }

  const handleEdit = (overtimeData) => {
    navigate('/timesheets/overtime/edit', { state: { overtimeData } })
  }

  const handleView = (overtimeData) => {
    navigate('/timesheets/overtime/view', { state: { overtimeData } })
  }

  const handleDelete = (overtimeData) => {
    setDeleteItem(overtimeData)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      toast.info('Deleting overtime record...')
      const response = await overtimeService.deleteOvertime(deleteItem._id)
      if (response.success) {
        toast.success('Overtime record deleted successfully!', { autoClose: 2000 })
        loadData() // Reload data
      } else {
        toast.error(response.message || 'Failed to delete overtime record')
      }
    } catch (error) {
      console.error('Error deleting overtime record:', error)
      if (error.response?.status === 404) {
        toast.error('Overtime record not found')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to delete this overtime record')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to delete overtime record. Please try again.')
        }
      }
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const cancelDelete = () => {
    setShowDeleteModal(false)
    setDeleteItem(null)
  }

  const handleExport = () => {
    toast.info('Export functionality coming soon!')
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb
        items={[
          { label: 'Home', path: '/' },
          { label: 'Timesheets', path: '/timesheets' },
          { label: 'Overtime', isCurrent: true }
        ]}
      />

      <div className="page-header">
        <div className="page-title">
          <h1>Overtime</h1>
          <p>Overtime hours and compensation tracking</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn-primary" onClick={handleAdd}>
            <Plus size={16} />
            New Overtime
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleExport}>
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      <div className="dashboard-metrics-grid">
        <div className="metric-card deals">
          <div className="metric-header">
            <div className="metric-icon">
              <ClipboardList size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalOvertimeRecords || 0}</div>
          <div className="metric-title">Total Records</div>
          <div className="metric-subtitle">All overtime entries</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <Timer size={16} />
            </div>
            <div className="metric-trend negative">
              <span>Hours</span>
            </div>
          </div>
          <div className="metric-value">{formatHours(stats.totalOvertimeHours)}</div>
          <div className="metric-title">Total Overtime Hours</div>
          <div className="metric-subtitle">Extra hours logged</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <DollarSign size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Pay</span>
            </div>
          </div>
          <div className="metric-value">{formatCurrency(stats.totalCompensationAmount)}</div>
          <div className="metric-title">Total Compensation</div>
          <div className="metric-subtitle">Compensation amount</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <Clock size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Pending</span>
            </div>
          </div>
          <div className="metric-value">{stats.draftRecords + stats.submittedRecords || 0}</div>
          <div className="metric-title">Pending Approval</div>
          <div className="metric-subtitle">Draft or submitted</div>
        </div>

        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <CheckCircle size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Approved</span>
            </div>
          </div>
          <div className="metric-value">{stats.approvedRecords || 0}</div>
          <div className="metric-title">Approved</div>
          <div className="metric-subtitle">Approved records</div>
        </div>

        <div className="metric-card conversion">
          <div className="metric-header">
            <div className="metric-icon">
              <DollarSign size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Paid</span>
            </div>
          </div>
          <div className="metric-value">{stats.paidRecords || 0}</div>
          <div className="metric-title">Paid</div>
          <div className="metric-subtitle">Compensation paid</div>
        </div>
      </div>

      <div className="data-table-section">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          enableSelection={false}
          noCard={true}
          pageSize={10}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Overtime Record"
        message={`Are you sure you want to delete overtime record "${deleteItem?.overtimeNumber}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default Overtime

